import { useState, useMemo } from "react";
import {
  Area, AreaChart, CartesianGrid,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { KpiCard } from "@/components/memory/KpiCard";
import {
  useTokenCost,
  type TokenCostFilters,
  type TokenCostItem,
} from "@/hooks/useTokenCost";
import { CostDistribution } from "./CostDistribution";
import { estimateCost, formatCost, formatTokens } from "./pricing";
import { PeriodFilter, sinceDate } from "./PeriodFilter";
import {
  DollarSign, Activity, Database, Loader2, Zap,
} from "lucide-react";

// --- KPI Cards ---

function KpiRow({ items, modelItems }: { items: TokenCostItem[]; modelItems: TokenCostItem[] }) {
  const totals = useMemo(() => {
    let input = 0, output = 0, cacheRead = 0, cacheWrite = 0, events = 0;
    for (const item of items) {
      input += item.input_tokens;
      output += item.output_tokens;
      cacheRead += item.cache_read;
      cacheWrite += item.cache_write;
      events += item.event_count;
    }
    const totalInputTokens = input + cacheRead + cacheWrite;
    const cacheHitRate = totalInputTokens > 0
      ? (cacheRead / totalInputTokens) * 100
      : 0;
    // Use model-grouped items for cost so each row gets per-model pricing.
    // Date-grouped rows have model=null and would fall back to Opus 4 (DEFAULT_PRICING),
    // causing ~18× overestimate (RAISE-16184).
    const cost = modelItems.length > 0
      ? modelItems.reduce((sum, item) => sum + estimateCost(item), 0)
      : estimateCost({ input_tokens: input, output_tokens: output, cache_read: cacheRead, cache_write: cacheWrite });
    return { output, cacheHitRate, events, cost };
  }, [items, modelItems]);

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <KpiCard
        title="Output Tokens"
        value={formatTokens(totals.output)}
        subtitle="total output"
        icon={<Zap className="h-4 w-4" />}
        tooltip="Total de tokens generados en el período"
      />
      <KpiCard
        title="Cache Hit Rate"
        value={`${totals.cacheHitRate.toFixed(1)}%`}
        subtitle="read vs total"
        icon={<Database className="h-4 w-4" />}
        accent={totals.cacheHitRate >= 50 ? "success" : "default"}
        tooltip="% de tokens servidos desde caché vs. procesados fresh"
      />
      <KpiCard
        title="Sessions"
        value={totals.events.toString()}
        subtitle="token events"
        icon={<Activity className="h-4 w-4" />}
        tooltip="Sesiones de trabajo registradas (eventos de token)"
      />
      <KpiCard
        title="Est. Cost"
        value={formatCost(totals.cost)}
        subtitle="based on published pricing"
        icon={<DollarSign className="h-4 w-4" />}
        tooltip="Costo estimado basado en precios publicados del modelo"
      />
    </div>
  );
}

// --- Daily Trend ---

function DailyTrend({ items }: { items: TokenCostItem[] }) {
  const [showCache, setShowCache] = useState(false);

  const data = useMemo(
    () => items.map((d) => ({
      date: d.date ?? "",
      input: d.input_tokens,
      output: d.output_tokens,
      cacheWrite: d.cache_write,
      cacheRead: d.cache_read,
    })),
    [items],
  );

  if (data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium">Daily Trend</CardTitle>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showCache}
            onChange={(e) => setShowCache(e.target.checked)}
            className="rounded border-border"
          />
          Show cache
        </label>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickFormatter={(v: string) => v.replace(/^\d{4}-/, "")}
            />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={formatTokens} />
            <Tooltip
              formatter={(val: number) => formatTokens(val)}
              labelFormatter={(l: string) => `Date: ${l}`}
            />
            {showCache && (
              <Area
                type="monotone"
                dataKey="cacheRead"
                stackId="1"
                fill="hsl(210 40% 80%)"
                stroke="hsl(210 40% 70%)"
                fillOpacity={0.3}
                name="Cache read"
              />
            )}
            <Area
              type="monotone"
              dataKey="cacheWrite"
              stackId="1"
              fill="hsl(215 20.2% 65.1%)"
              stroke="hsl(215 20.2% 55.1%)"
              fillOpacity={0.5}
              name="Cache write"
            />
            <Area
              type="monotone"
              dataKey="output"
              stackId="1"
              fill="hsl(222.2 47.4% 11.2%)"
              stroke="hsl(222.2 47.4% 11.2%)"
              fillOpacity={0.8}
              name="Output"
            />
            <Area
              type="monotone"
              dataKey="input"
              stackId="1"
              fill="hsl(142 76% 36%)"
              stroke="hsl(142 76% 30%)"
              fillOpacity={0.7}
              name="Input"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// --- Empty State ---

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
      <DollarSign className="h-10 w-10 text-muted-foreground/40 mb-4" />
      <h3 className="text-sm font-medium text-foreground mb-1">No token data yet</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Token usage is recorded when sessions close. Close a few sessions with{" "}
        <code className="text-xs bg-muted px-1 py-0.5 rounded">rai session close</code>{" "}
        to start tracking costs.
      </p>
    </div>
  );
}

// --- Main Panel ---

interface TokenCostPanelProps {
  projectSlug: string | null;
  /** When provided, the panel uses this period and hides its internal PeriodFilter. */
  externalPeriodDays?: number;
}

export function TokenCostPanel({ projectSlug, externalPeriodDays }: TokenCostPanelProps) {
  const [internalPeriodDays, setInternalPeriodDays] = useState(30);
  const periodDays = externalPeriodDays ?? internalPeriodDays;
  const filters: TokenCostFilters = useMemo(
    () => ({ since: sinceDate(periodDays) }),
    [periodDays],
  );

  const dateData = useTokenCost(projectSlug, "date", filters);
  const modelData = useTokenCost(projectSlug, "model", filters);
  const phaseData = useTokenCost(projectSlug, "phase", filters);

  const loading = dateData.loading || modelData.loading || phaseData.loading;
  const error = dateData.error || modelData.error || phaseData.error;
  const hasData = dateData.items.length > 0;

  if (loading && !hasData) {
    return (
      <div className="flex h-[400px] items-center justify-center rounded-lg border text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading token data...
      </div>
    );
  }

  if (error && !hasData) {
    return (
      <div className="flex h-[400px] items-center justify-center rounded-lg border">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!hasData) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Period Filter (hidden when externally controlled) */}
      {externalPeriodDays === undefined && (
        <PeriodFilter value={periodDays} onChange={setInternalPeriodDays} />
      )}

      {/* KPIs */}
      <KpiRow items={dateData.items} modelItems={modelData.items} />

      {/* Daily Trend */}
      <DailyTrend items={dateData.items} />

      {/* Where the Money Goes */}
      <CostDistribution modelItems={modelData.items} phaseItems={phaseData.items} />
    </div>
  );
}
